/**
 * Nạp TOÀN BỘ kết quả của bộ lọc đang mở, để xuất ra tệp.
 *
 * Vòng lặp phân trang này trước đây được CHÉP TAY ở bốn trang (PI, hoá đơn,
 * phiếu giao, packing list) và viết lần thứ năm, chặt chẽ hơn hẳn, cho sản
 * phẩm. Năm bản chép là năm chỗ có thể sai khác nhau — và chúng đã sai khác
 * thật. Đây là bản dùng chung, giữ theo bản chặt nhất.
 *
 * VÌ SAO PHẢI CHẶT. Phân trang theo offset không an toàn khi dữ liệu đổi giữa
 * chừng: ai đó thêm một bản ghi trong lúc ta đang ở trang 3 thì trang 4 lặp
 * lại một dòng và làm MẤT một dòng khác. Không có lỗi nào hiện ra — người dùng
 * nhận một tệp thiếu và tin là đủ. Nên thà báo "dữ liệu vừa thay đổi, xuất
 * lại" còn hơn giao một tệp sai âm thầm.
 *
 * Phần thuần này không đụng mạng: `fetchPage` được truyền vào, nên kiểm thử
 * được toàn bộ đường biên mà không cần máy chủ giả.
 */


/**
 * Nạp 200 dòng mỗi lượt. Lớn hơn trang người dùng đang xem (25–100) để bớt số
 * vòng, nhưng vẫn đủ nhỏ để không nặng một phản hồi.
 */
export const EXPORT_PAGE_SIZE = 200;

/**
 * Các endpoint ERP phân trang CỐ ĐỊNH 50 dòng và không nhận tham số kích thước
 * trang. Bộ nạp phải biết con số đó, vì nó kiểm "mỗi trang trả đúng bao nhiêu
 * dòng" — hỏi 200 mà nhận 50 sẽ bị hiểu nhầm là dữ liệu vừa thay đổi.
 */
export const ERP_PAGE_SIZE = 50;

/**
 * Trần cứng số dòng.
 *
 * Dựng chuỗi CSV hàng trăm nghìn dòng trong trình duyệt sẽ treo tab. Chạm trần
 * là DỪNG và báo lỗi, không cắt bớt im lặng: một tệp thiếu dòng mà không ai
 * biết còn tệ hơn một lần xuất thất bại.
 */
export const EXPORT_ROW_CAP = 50_000;

/** Dữ liệu đã đổi giữa chừng — tệp sẽ không toàn vẹn, phải xuất lại. */
export const EXPORT_CHANGED = "changed";
/** Quá nhiều dòng: người dùng cần lọc hẹp lại. */
export const EXPORT_TOO_MANY = "tooMany";

/**
 * Phản hồi của một trang.
 *
 * `page`/`pageSize` là TUỲ CHỌN: các endpoint ERP phân trang cố định 50 dòng
 * và không dội lại hai trường đó. Guard nào có dữ liệu thì chạy, không có thì
 * bỏ qua đúng guard ấy — chứ không bắt mọi endpoint phải đổi hình dạng.
 */
export interface PagedResponse<T> {
  data: T[];
  total: number;
  page?: number;
  pageSize?: number;
}

export type PageFetcher<T> = (page: number, pageSize: number) => Promise<PagedResponse<T>>;

export interface CollectExportOptions<T> {
  /** Tổng số dòng mà màn hình đang hiển thị. Mọi trang phải khai đúng con số này. */
  expectedTotal: number;
  fetchPage: PageFetcher<T>;
  signal?: AbortSignal;
  onProgress?: (completed: number, total: number, verifying: boolean) => void;
  pageSize?: number;
  /**
   * Khóa nhận dạng một dòng, dùng để phát hiện cùng một bản ghi hiện ở hai
   * trang. Mặc định đọc `id`. Danh sách nào không có id ổn định thì trả chuỗi
   * rỗng và bỏ qua RIÊNG phép kiểm này — ba guard còn lại (tổng ổn định, đúng
   * trang đã hỏi, đủ số dòng) vẫn chạy.
   */
  keyOf?: (row: T) => string;
  /**
   * Nạp lượt thứ hai và đối chiếu từng dòng.
   *
   * Mặc định: chỉ khi kết quả trải trên NHIỀU trang. Gọn trong một trang thì
   * không có khe nào để dữ liệu trôi giữa hai lần gọi, nên lượt hai chỉ tốn
   * thêm một vòng mạng mà không phát hiện được gì.
   */
  verify?: boolean;
}

export async function collectExportRows<T>({
  expectedTotal,
  fetchPage,
  signal,
  onProgress,
  pageSize = EXPORT_PAGE_SIZE,
  verify,
  keyOf = (row) => (row as { id?: string }).id ?? "",
}: CollectExportOptions<T>): Promise<T[]> {
  if (!Number.isSafeInteger(expectedTotal) || expectedTotal < 0) throw new Error(EXPORT_CHANGED);
  if (expectedTotal > EXPORT_ROW_CAP) throw new Error(EXPORT_TOO_MANY);

  const pageCount = Math.max(1, Math.ceil(expectedTotal / pageSize));
  const passes = (verify ?? pageCount > 1) ? 2 : 1;
  const rows: T[] = [];
  const ids = new Set<string>();
  const abort = () => {
    if (signal?.aborted) throw new DOMException("Cancelled", "AbortError");
  };

  for (let pass = 0; pass < passes; pass++) {
    for (let page = 1; page <= pageCount; page++) {
      abort();
      const result = await fetchPage(page, pageSize);
      abort();

      const offset = (page - 1) * pageSize;
      const expectedLength = Math.min(pageSize, Math.max(0, expectedTotal - offset));
      // Tổng đổi, hoặc trang trả về không đúng trang đã hỏi, hoặc số dòng
      // không khớp — cả ba đều nghĩa là bảng dưới chân đã dịch chuyển.
      if (result.total !== expectedTotal) throw new Error(EXPORT_CHANGED);
      if (result.page !== undefined && result.page !== page) throw new Error(EXPORT_CHANGED);
      if (result.pageSize !== undefined && result.pageSize !== pageSize)
        throw new Error(EXPORT_CHANGED);
      if (result.data.length !== expectedLength) throw new Error(EXPORT_CHANGED);

      for (let index = 0; index < result.data.length; index++) {
        const row = result.data[index]!;
        if (pass === 0) {
          // Cùng một id hiện ở hai trang: dấu hiệu chắc chắn của trôi trang.
          const key = keyOf(row);
          if (key) {
            if (ids.has(key)) throw new Error(EXPORT_CHANGED);
            ids.add(key);
          }
          rows.push(row);
        } else if (JSON.stringify(row) !== JSON.stringify(rows[offset + index])) {
          throw new Error(EXPORT_CHANGED);
        }
      }
      onProgress?.(offset + result.data.length, expectedTotal, pass === passes - 1 && passes > 1);
    }
  }
  return rows;
}

/**
 * Khoá i18n cho lỗi khi nạp — để mọi màn hình báo cùng một câu.
 * Lỗi khác (mất mạng, 403) đi qua `getLocalizedError` như bình thường.
 */
export function exportErrorKey(error: unknown): string | null {
  if (!(error instanceof Error)) return null;
  if (error.message === EXPORT_CHANGED) return "dataExport.changed";
  if (error.message === EXPORT_TOO_MANY) return "dataExport.tooMany";
  return null;
}
